import { Link, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Lock, ArrowLeft } from "lucide-react";

function AccessDeniedPage({ module }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F7F7F6] dark:bg-neutral-950 p-6" data-testid="access-denied-page">
      <div className="max-w-md w-full border border-[#0D0D0D] dark:border-neutral-800 bg-white dark:bg-neutral-900 p-8">
        <div className="flex items-center gap-2 mb-4">
          <Lock className="h-5 w-5 text-[#FF3B30]" />
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF3B30]" data-testid="access-denied-status">
            403 · Access denied
          </div>
        </div>
        <div className="font-heading text-3xl mb-2" data-testid="access-denied-title">Locked.</div>
        <div className="text-sm text-[#5C5C5C] mb-6">
          You don&apos;t have permission to view this page. Ask your admin to enable the
          {module ? <> <span className="font-mono text-[11px] px-1.5 py-0.5 border border-[#0D0D0D] dark:border-neutral-700">{module}</span> </> : " required "}
          module for your account.
        </div>
        <Link
          to="/dashboard"
          data-testid="access-denied-back-link"
          className="inline-flex items-center gap-2 text-sm font-bold border border-[#0D0D0D] dark:border-neutral-700 px-4 py-2 hover:bg-[#0D0D0D] hover:text-white dark:hover:bg-white dark:hover:text-black"
        >
          <ArrowLeft className="h-4 w-4" /> Back to dashboard
        </Link>
      </div>
    </div>
  );
}

export default function ProtectedRoute({ children, roles, module, redirectTo }) {
  const { user, loading, hasModule } = useAuth();
  const location = useLocation();

  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F7F6]">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-neutral-500" data-testid="route-loading">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (Array.isArray(roles) && roles.length > 0 && !roles.includes(user.role)) {
    // ``redirectTo`` lets a route silently bounce non-matching roles
    // (e.g. Manager visiting /admin → /dashboard) instead of showing
    // a scary "Locked" page for something they simply shouldn't see.
    if (redirectTo) return <Navigate to={redirectTo} replace />;
    return <AccessDeniedPage module={module} />;
  }
  // Module-level gating (25-module catalog). Admins always pass hasModule().
  if (module && !hasModule(module)) {
    // Even on /dashboard, show the same 403 page (no infinite loop possible
    // because the redirect target is no longer /dashboard).
    if (location.pathname === "/dashboard") {
      return (
        <div className="min-h-screen flex items-center justify-center bg-[#F7F7F6] dark:bg-neutral-950 p-6" data-testid="no-module-access">
          <div className="max-w-md text-center border border-[#0D0D0D] dark:border-neutral-800 p-8">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF3B30] mb-2">/ access restricted</div>
            <div className="font-heading text-2xl mb-2">No modules assigned</div>
            <div className="text-sm text-[#5C5C5C]">Ask your admin to enable at least one module for your account.</div>
          </div>
        </div>
      );
    }
    return <AccessDeniedPage module={module} />;
  }
  return children;
}
